/**
 * The OWNER'S LANE on an outreach thread — what the owner told their agent, chose, approved, declined,
 * answered or closed — and the pieces of the "Tell {agent}" composer that are rules rather than looks
 * (outreach visibility phase 4, design `docs/2026-10-17-outreach-visibility-design.md` §5–§6).
 *
 * The server sends FACTS on `OutreachThreadRow.ownerEvents` (prefixed enums, timestamps, the owner's own
 * words verbatim); this module maps them onto catalog keys and places them in the transcript. It returns
 * SHAPES, never strings — the outreach-stage convention (`lib/outreach-stage.ts`) — and `renderLaneLine`
 * is the thin step to a string.
 *
 * ⚠️ A cross-surface CONTRACT. The same function names live in iOS `Store/OutreachLane.swift` and Android
 * `OutreachLane.kt`, and all three prove themselves against ONE fixture,
 * `native-apps/shared/outreach-composer/contract.json` (`lib/outreach-lane.test.ts` here). Change a rule
 * and you change the fixture and all three.
 *
 * No runtime imports, deliberately: a jest suite loads this module directly, and anything that reaches
 * common-lib breaks jest in CI (see orion-web CLAUDE.md "Tests (jest)").
 */

// ── Vocabulary ─────────────────────────────────────────────────────────────────────────────────

export type OwnerEventKind = 'told' | 'chose' | 'approved' | 'declined' | 'answered' | 'closed';
export type OwnerNoteState = 'pending' | 'sent' | 'handled' | 'failed';

/** One `OutreachOwnerEvent` as it arrives on the wire (BFF field 21). Every field may be absent. */
export interface WireOwnerEvent {
  /** `OUTREACH_OWNER_EVENT_*` (prefix optional, any case). */
  kind?: string | null;
  /** The owner's own words — a note, the option they chose, their answer. Verbatim. */
  text?: string;
  at?: string;
  decisionId?: string;
  noteId?: string;
  /** `OUTREACH_OWNER_NOTE_STATE_*` — set on a TOLD only. */
  noteState?: string | null;
  /** The owner's conversation the note was handed to. */
  conversationId?: string;
}

export type LaneParam = { kind: 'text'; value: string } | { kind: 'key'; key: string };

export interface LaneAction {
  kind: 'openChat';
  labelKey: 'outreach.lane.openChat';
  conversationId: string;
}

/** A lane line — the outreach-stage `{kind:'key', key, params, suffix?}` plus an optional action. */
export interface LaneLine {
  kind: 'key';
  key: string;
  params: Record<string, LaneParam>;
  suffix?: string;
  action?: LaneAction;
}

/** Past this, a still-PENDING note offers the conversation it was handed to (`staleOpenChatMs`). */
export const STALE_OPEN_CHAT_MS = 120_000;

// ── Parsing ────────────────────────────────────────────────────────────────────────────────────

function enumWord(raw: string | null | undefined, prefix: string): string | null {
  const value = (raw ?? '').trim().toUpperCase();
  if (!value) return null;
  const bare = value.startsWith(prefix) ? value.slice(prefix.length) : value;
  return bare && bare !== 'UNKNOWN' ? bare : null;
}

const KIND_WORDS: Record<string, OwnerEventKind> = {
  TOLD: 'told',
  CHOSE: 'chose',
  APPROVED: 'approved',
  DECLINED: 'declined',
  ANSWERED: 'answered',
  CLOSED: 'closed',
};
const STATE_WORDS: Record<string, OwnerNoteState> = {
  PENDING: 'pending',
  SENT: 'sent',
  HANDLED: 'handled',
  FAILED: 'failed',
};

/** `OUTREACH_OWNER_EVENT_*` (prefix optional, any case) → a kind; UNKNOWN / empty / unrecognised → null. */
export function parseOwnerEventKind(raw: string | null | undefined): OwnerEventKind | null {
  const word = enumWord(raw, 'OUTREACH_OWNER_EVENT_');
  return (word && KIND_WORDS[word]) || null;
}

/** `OUTREACH_OWNER_NOTE_STATE_*` (prefix optional, any case) → a state; UNKNOWN / empty / unrecognised → null. */
export function parseOwnerNoteState(raw: string | null | undefined): OwnerNoteState | null {
  const word = enumWord(raw, 'OUTREACH_OWNER_NOTE_STATE_');
  return (word && STATE_WORDS[word]) || null;
}

function atMs(at: string | undefined): number | null {
  if (!at) return null;
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? ms : null;
}

const text = (value: string): LaneParam => ({ kind: 'text', value });
const hasText = (value: string | undefined): value is string => typeof value === 'string' && value.trim().length > 0;

/** The line with no time-dependent part, or null when the event is dropped (no kind, no `at`, empty words). */
function baseLine(event: WireOwnerEvent): LaneLine | null {
  const kind = parseOwnerEventKind(event.kind);
  if (!kind || atMs(event.at) === null) return null;
  switch (kind) {
    case 'told':
      return hasText(event.text) ? { kind: 'key', key: 'outreach.lane.told', params: { text: text(event.text) } } : null;
    case 'chose':
      return hasText(event.text) ? { kind: 'key', key: 'outreach.lane.chose', params: { choice: text(event.text) } } : null;
    case 'answered':
      return hasText(event.text) ? { kind: 'key', key: 'outreach.lane.answered', params: { text: text(event.text) } } : null;
    case 'approved':
      return { kind: 'key', key: 'outreach.lane.approved', params: {} };
    case 'declined':
      return { kind: 'key', key: 'outreach.lane.declined', params: {} };
    case 'closed':
      return { kind: 'key', key: 'outreach.line.closedByYou', params: {} };
  }
}

/** Whether an event renders at all — the same drop rule `laneLabel` applies, without a clock. */
export function isLaneEvent(event: WireOwnerEvent): boolean {
  return baseLine(event) !== null;
}

/**
 * The line one owner event renders as, or null for "render nothing". A TOLD carries its note's state as a
 * suffix (SENT carries none: the sent words are the agent's own message in the transcript), and a PENDING
 * note older than two minutes (strictly) with a conversation to open offers it.
 */
export function laneLabel(event: WireOwnerEvent, now: Date = new Date()): LaneLine | null {
  const line = baseLine(event);
  if (!line || parseOwnerEventKind(event.kind) !== 'told') return line;
  const state = parseOwnerNoteState(event.noteState);
  if (state === 'pending') {
    const conversationId = (event.conversationId ?? '').trim();
    const stale = now.getTime() - (atMs(event.at) as number) > STALE_OPEN_CHAT_MS;
    return stale && conversationId
      ? { ...line, suffix: 'outreach.lane.pending', action: { kind: 'openChat', labelKey: 'outreach.lane.openChat', conversationId } }
      : { ...line, suffix: 'outreach.lane.pending' };
  }
  if (state === 'handled') return { ...line, suffix: 'outreach.lane.handled' };
  if (state === 'failed') return { ...line, suffix: 'outreach.lane.failed' };
  return line;
}

// ── The transcript ─────────────────────────────────────────────────────────────────────────────

export interface LaneMessage {
  sentAt?: string;
}

export type TranscriptItem<M> =
  | { kind: 'message'; message: M; index: number }
  | { kind: 'event'; event: WireOwnerEvent; source: 'server' | 'optimistic'; index: number };

/** The thread's owner events that render, sorted by `at` (wire order kept on equal times). */
export function ownerLaneItems(thread: { ownerEvents?: WireOwnerEvent[] | null } | null | undefined): WireOwnerEvent[] {
  return (thread?.ownerEvents ?? [])
    .map((event, index) => ({ event, index, at: atMs(event.at) }))
    .filter(({ event }) => isLaneEvent(event))
    .sort((a, b) => (a.at as number) - (b.at as number) || a.index - b.index)
    .map(({ event }) => event);
}

/**
 * Messages and owner events as ONE transcript.
 *
 * 1. Unrenderable events are dropped; the rest sort by `at` as instants, wire order kept on equal times.
 * 2. Optimistic items (the composer's own TOLD/PENDING) join the sort — unless the server's events already
 *    hold that `noteId` — and on an exact tie the server's event goes first.
 * 3. Messages are walked in WIRE order and never re-sorted. Before each message with a `sentAt`, every
 *    remaining event strictly earlier is emitted; a message with no `sentAt` stays in place and takes no
 *    event. What is left follows the last message. So on a tie the MESSAGE goes first.
 */
export function mergeTranscript<M extends LaneMessage>(
  messages: M[] | null | undefined,
  ownerEvents: WireOwnerEvent[] | null | undefined,
  optimistic?: WireOwnerEvent[] | null,
): TranscriptItem<M>[] {
  const server = ownerEvents ?? [];
  const serverNoteIds = new Set(server.map((event) => event.noteId).filter((id): id is string => !!id));
  const queue = [
    ...server.map((event, index) => ({ event, index, source: 'server' as const, rank: 0 })),
    ...(optimistic ?? [])
      .map((event, index) => ({ event, index, source: 'optimistic' as const, rank: 1 }))
      .filter(({ event }) => !(event.noteId && serverNoteIds.has(event.noteId))),
  ]
    .filter(({ event }) => isLaneEvent(event))
    .map((item) => ({ ...item, at: atMs(item.event.at) as number }))
    .sort((a, b) => a.at - b.at || a.rank - b.rank || a.index - b.index);

  const out: TranscriptItem<M>[] = [];
  let next = 0;
  const emitBefore = (limit: number) => {
    while (next < queue.length && queue[next].at < limit) {
      const { event, source, index } = queue[next++];
      out.push({ kind: 'event', event, source, index });
    }
  };
  (messages ?? []).forEach((message, index) => {
    const sent = atMs(message.sentAt);
    if (sent !== null) emitBefore(sent);
    out.push({ kind: 'message', message, index });
  });
  emitBefore(Number.POSITIVE_INFINITY);
  return out;
}

// ── The composer's visibility and refetch stop rule ────────────────────────────────────────────

/** Every thread gets the composer — DONE included — except one whose read failed or that has no id. */
export function composerVisible(thread: { outreachId?: string | null } | null | undefined, readFailed: boolean): boolean {
  return !readFailed && !!(thread?.outreachId ?? '').trim();
}

/** The note states that end the composer's refetch schedule. */
const SETTLED_STATES: readonly OwnerNoteState[] = ['sent', 'handled', 'failed'];

/** Thread scope: the lane holds `noteId` in a settled state (SENT / HANDLED / FAILED). */
export function noteSettledOnThread(thread: { ownerEvents?: WireOwnerEvent[] | null } | null | undefined, noteId: string): boolean {
  if (!noteId) return false;
  return (thread?.ownerEvents ?? []).some((event) => {
    if (event.noteId !== noteId) return false;
    const state = parseOwnerNoteState(event.noteState);
    return state !== null && SETTLED_STATES.includes(state);
  });
}

type LaneEntry = { outbound?: { ownerEvents?: WireOwnerEvent[] | null }[]; inbound?: { ownerEvents?: WireOwnerEvent[] | null }[] };

/** Contact scope: the server's event for `noteId` on any thread of the contact, once a re-read carries it. */
export function noteEventOnContact(entry: LaneEntry | null | undefined, noteId: string): WireOwnerEvent | undefined {
  if (!noteId || !entry) return undefined;
  for (const thread of [...(entry.outbound ?? []), ...(entry.inbound ?? [])]) {
    const hit = (thread.ownerEvents ?? []).find((event) => event.noteId === noteId);
    if (hit) return hit;
  }
  return undefined;
}

/** Contact scope: any thread of the contact whose owner events hold `noteId` (the note found its thread). */
export function noteLandedOnContact(entry: LaneEntry | null | undefined, noteId: string): boolean {
  return noteEventOnContact(entry, noteId) !== undefined;
}

// ── The chip in the Maestro chat ───────────────────────────────────────────────────────────────

/** `metadata.outreachComposer` on a USER row the composer dispatched. */
export interface OutreachComposerMarker {
  noteId?: string;
  outreachId?: string;
  recipientId?: string;
}

/** Reads the marker off a row's metadata — a plain object or nothing, never a guess. */
export function readOutreachComposerMarker(metadata: unknown): OutreachComposerMarker | undefined {
  if (!metadata || typeof metadata !== 'object') return undefined;
  const raw = (metadata as { outreachComposer?: unknown }).outreachComposer;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const pick = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
  const marker = raw as Record<string, unknown>;
  const out: OutreachComposerMarker = {};
  const noteId = pick(marker.noteId);
  const outreachId = pick(marker.outreachId);
  const recipientId = pick(marker.recipientId);
  if (noteId) out.noteId = noteId;
  if (outreachId) out.outreachId = outreachId;
  if (recipientId) out.recipientId = recipientId;
  return out;
}

export type ChipContactId = { personId: string } | { recipientId: string };

export interface ComposerChip {
  line: { kind: 'key'; key: 'outreach.composer.chip'; params: { name: LaneParam } };
  target: { screen: 'contactThread'; id: ChipContactId; outreachId: string } | { screen: 'contactDetail'; id: ChipContactId };
}

interface ChipThread {
  outreachId?: string;
}
interface ChipEntry {
  contact?: { personId?: string; displayName?: string };
  recipientId?: string;
  outbound?: ChipThread[];
  inbound?: ChipThread[];
}

function chipId(entry: ChipEntry): ChipContactId | null {
  const personId = (entry.contact?.personId ?? '').trim();
  if (personId) return { personId };
  const recipientId = (entry.recipientId ?? '').trim();
  return recipientId ? { recipientId } : null;
}

/**
 * The chip under a composer row in the Maestro chat: "About {name}'s conversation", opening the thread
 * (by `outreachId`, across outbound and inbound) or the contact (by `recipientId`). Null — no chip — for
 * a non-USER row, a row with no marker (a plain `metadata.outreachId` selection row is not one), a marker
 * with no target, and a target the contact book does not hold or cannot name.
 */
export function composerChip(row: { role?: string | null; metadata?: unknown }, contacts: ChipEntry[] | null | undefined): ComposerChip | null {
  if ((row.role ?? '').trim().toUpperCase() !== 'USER') return null;
  const marker = readOutreachComposerMarker(row.metadata);
  if (!marker || (!marker.outreachId && !marker.recipientId)) return null;
  const entries = contacts ?? [];
  const named = (entry: ChipEntry): { name: string; id: ChipContactId } | null => {
    const name = (entry.contact?.displayName ?? '').trim();
    const id = chipId(entry);
    return name && id ? { name, id } : null;
  };
  if (marker.outreachId) {
    const outreachId = marker.outreachId;
    const entry = entries.find((candidate) =>
      [...(candidate.outbound ?? []), ...(candidate.inbound ?? [])].some((thread) => thread.outreachId === outreachId),
    );
    const hit = entry ? named(entry) : null;
    if (hit) {
      return {
        line: { kind: 'key', key: 'outreach.composer.chip', params: { name: text(hit.name) } },
        target: { screen: 'contactThread', id: hit.id, outreachId },
      };
    }
  }
  if (marker.recipientId) {
    const entry = entries.find((candidate) => (candidate.recipientId ?? '').trim() === marker.recipientId);
    const hit = entry ? named(entry) : null;
    if (hit)
      return {
        line: { kind: 'key', key: 'outreach.composer.chip', params: { name: text(hit.name) } },
        target: { screen: 'contactDetail', id: hit.id },
      };
  }
  return null;
}

// ── Rendering ──────────────────────────────────────────────────────────────────────────────────

export type LaneTranslate = (key: string, params?: Record<string, unknown>) => string;

/**
 * A lane line as a string: `t(key, params)`, then `" · " + t(suffix)` when it has one. `{maestroAgentName}`
 * is the caller's (`useAgentName().name`) and reaches the suffix too — "{maestroAgentName} is on it".
 */
export function renderLaneLine(
  line: { key: string; params: Record<string, LaneParam>; suffix?: string },
  t: LaneTranslate,
  maestroAgentName: string,
): string {
  const params: Record<string, unknown> = { maestroAgentName };
  for (const [name, param] of Object.entries(line.params)) params[name] = param.kind === 'text' ? param.value : t(param.key, { maestroAgentName });
  const body = t(line.key, params);
  return line.suffix ? `${body} · ${t(line.suffix, { maestroAgentName })}` : body;
}
